import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Typography, Select, Popconfirm, Flex, Tooltip } from 'antd';
import { BaseOptionType } from 'antd/es/select';
import { TFunction, useTranslation } from 'translations/hooks';

import { MAX_PARAMETERS, PlotterMode } from 'business/data-analysis/constants';
import { GraphChartType } from 'business/data-analysis/pages/graph/components/chart/types';
import {
  useGraphSet,
  useGraphSetDispatch,
} from 'business/data-analysis/pages/graph/hooks/graph-context/contexts';
import { GraphSetDispatchActions } from 'business/data-analysis/pages/graph/hooks/graph-context/types';
import { useGraph } from 'business/data-analysis/pages/graph/hooks/use-graph';
import { useMode } from 'business/data-analysis/pages/graph/hooks/use-mode';
import { displayTypes } from 'business/data-analysis/pages/graph/types';
import Button from 'ui/button';
import CrossIcon from 'ui/icons/cross';
import PencilIcon from 'ui/icons/pencil';

import styles from './index.module.scss';

interface Props {
  graphId: string;
  displayParameterList: boolean;
  toogleDisplayParameterList: () => void;
}

const graphChartTypeOptions = (t: TFunction) => {
  const graphChartTypes = Object.values(GraphChartType);

  return graphChartTypes.map((type) => {
    return {
      value: type,
      label: t('dataAnalysis.chartType.type', { context: type }),
      disabled: type === GraphChartType.CUSTOM,
    };
  });
};

const GraphCardHeader = ({
  graphId,
  displayParameterList,
  toogleDisplayParameterList,
}: Props) => {
  const { t } = useTranslation();
  const mode = useMode();
  const graph = useGraph(graphId);
  const {
    structure: { graphs },
  } = useGraphSet();
  const dispatch = useGraphSetDispatch();

  const tooManyParamToSwitchToGraphType =
    graph.displayType === 'table' && graph.parameters.size > MAX_PARAMETERS;

  const displayTypeOptions = [
    {
      value: displayTypes.graph,
      label: (
        <Tooltip
          title={
            tooManyParamToSwitchToGraphType
              ? t('dataAnalysis.warning.maxNumberOfParameters', {
                  context: 'limitCrossed',
                  numberOfParameters: graph.parameters.size,
                  maxParameterNumber: MAX_PARAMETERS,
                })
              : null
          }
        >
          <span>{t('dataAnalysis.graphType.graph')}</span>
        </Tooltip>
      ),

      disabled: tooManyParamToSwitchToGraphType,
    },
    {
      value: displayTypes.table,
      label: <span>{t('dataAnalysis.graphType.table')}</span>,
    },
  ] satisfies BaseOptionType;

  return (
    <Flex justify="space-between">
      <Typography.Text
        className={styles.cardTitle}
        editable={{
          icon: <PencilIcon className={styles.cardTitleAction} />,
          tooltip: t('dataAnalysis.graph.header.tooltip'),
          onChange: (title: string) =>
            dispatch({
              type: GraphSetDispatchActions.UpdateGraph,
              graphId,
              update: {
                title,
              },
            }),
          triggerType: ['icon'],
        }}
      >
        {graph.title !== ''
          ? graph.title
          : t('dataAnalysis.labels.newGraph', {
              graphNumber: graph.graphNumber,
            })}
      </Typography.Text>

      <Flex
        justify="space-between"
        align="center"
        gap={10}
        className={styles.actionContainer}
      >
        <Button
          onClick={() => toogleDisplayParameterList()}
          icon={
            displayParameterList ? <EyeInvisibleOutlined /> : <EyeOutlined />
          }
        >
          {t('dataAnalysis.graph.header.parameterListToogle', {
            context: displayParameterList ? 'hide' : 'show',
          })}
        </Button>
        {graph.displayType === 'graph' && mode === PlotterMode.RING ? (
          <Select
            className={styles.graphType}
            options={graphChartTypeOptions(t)}
            value={graph.chartType}
            onChange={(chartType) =>
              dispatch({
                type: GraphSetDispatchActions.UpdateGraph,
                graphId,
                update: {
                  chartType,
                },
              })
            }
          />
        ) : null}
        <Select
          className={styles.graphType}
          options={displayTypeOptions}
          value={graph.displayType}
          onChange={(displayType) =>
            dispatch({
              type: GraphSetDispatchActions.UpdateGraph,
              graphId,
              update: {
                displayType,
              },
            })
          }
        />

        <Popconfirm
          title={t('dataAnalysis.graph.header.delete.title')}
          okButtonProps={{ type: 'primary' }}
          placement="topRight"
          onConfirm={() =>
            dispatch({
              type: GraphSetDispatchActions.DeleteGraph,
              graphId: graphId,
            })
          }
        >
          <Button
            icon={<CrossIcon className={styles.actionIcon} />}
            disabled={graphs.size <= 1}
          />
        </Popconfirm>
      </Flex>
    </Flex>
  );
};

export default GraphCardHeader;
